'use client';

import { useEffect, useRef } from 'react';

type SiteClockProps = {
    locale: string;
    timeZone: string;
};

function formatTime(date: Date, locale: string, timeZone: string) {
    return new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
        timeZone,
    }).format(date);
}

export default function SiteClock({ locale, timeZone }: SiteClockProps) {
    const timeRef = useRef<HTMLTimeElement>(null);

    useEffect(() => {
        let minuteTimer = 0;

        const update = () => {
            const element = timeRef.current;
            if (!element) return;

            const currentTime = new Date();
            element.dateTime = currentTime.toISOString();
            element.textContent = formatTime(currentTime, locale, timeZone);
            element.classList.remove('site-header__clock-time--pending');
        };
        const schedule = () => {
            window.clearTimeout(minuteTimer);
            update();
            minuteTimer = window.setTimeout(schedule, 60_000 - (Date.now() % 60_000) + 50);
        };
        const updateWhenVisible = () => {
            if (!document.hidden) schedule();
        };

        schedule();

        window.addEventListener('focus', schedule);
        window.addEventListener('pageshow', schedule);
        window.addEventListener('popstate', schedule);
        document.addEventListener('visibilitychange', updateWhenVisible);

        return () => {
            window.clearTimeout(minuteTimer);
            window.removeEventListener('focus', schedule);
            window.removeEventListener('pageshow', schedule);
            window.removeEventListener('popstate', schedule);
            document.removeEventListener('visibilitychange', updateWhenVisible);
        };
    }, [locale, timeZone]);

    return (
        <div className='site-header__clocks'>
            <span className='site-header__clock'>
                <time
                    ref={timeRef}
                    className='site-header__clock-time site-header__clock-time--pending'
                >
                    --:--
                </time>
            </span>
        </div>
    );
}
